import React, { useMemo } from "react";
import { gql } from "@apollo/client";
import {
  useHrisEmployeeEventSidebarQuery,
  useHrisEmployeeEventPreviousQuery,
} from "./HrisEmployeeEventDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { format, parseISO } from "date-fns";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
} from "@src/lib/ui/QueryTabs";
import StyledJsonDiff from "@src/lib/ui/StyledJsonDiff";
import FormattedJson from "@src/lib/ui/FormattedJson";
import { HrisEmployeeEventsInput } from "@src/types.generated";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import Text from "@src/lib/ui/Text";

type Props = {
  rowId: number; // pghId is a number
  hrisEmployeeId?: number; // Optional: not needed anymore but kept for compatibility
  showHeader?: boolean;
};

const HrisEmployeeEventDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  // Query the singular HrisEmployeeEvent by pghId
  const { data, loading } = useHrisEmployeeEventSidebarQuery({
    variables: {
      pghId: rowId,
    },
    skip: !rowId,
  });

  const event = data?.HrisEmployeeEvent;

  // Query for the previous event using pghIdLte filter
  const previousEventInput: HrisEmployeeEventsInput | null = useMemo(() => {
    if (!event?.id) return null;
    return {
      filters: {
        hrisEmployeeId: { value: event.id },
        pghIdLte: { value: rowId - 1 }, // Get events with pghId less than or equal to current - 1
      },
      sort: { column: "pghId", descending: true }, // Get most recent previous event
    };
  }, [event?.id, rowId]);

  const { data: previousEventData, loading: previousLoading } =
    useHrisEmployeeEventPreviousQuery({
      variables: {
        input: previousEventInput!,
        pagination: { limit: 1 }, // Only need the most recent previous event
      },
      skip: !previousEventInput || !event?.id,
    });

  const previousEvent =
    previousEventData?.HrisEmployeeEvents?.objects?.[0] || null;

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-muted-foreground">
          Loading event data...
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-muted-foreground">Event not found</div>
      </div>
    );
  }

  const rawHrisData = event.rawHrisData ?? {};
  const previousRawHrisData = previousEvent?.rawHrisData ?? {};
  const timestamp = event.pghCreatedAt
    ? format(parseISO(event.pghCreatedAt), "M/d/yyyy h:mm a")
    : "";

  const items: InfoTableItem[] = [
    {
      label: "Timestamp",
      value: timestamp || "--",
    },
    {
      label: "Event ID",
      value: event.pghId.toString(),
    },
    {
      label: "External HRIS ID",
      value: event.remoteHrisId || "--",
    },
    {
      label: "First name",
      value: event.firstName || "--",
    },
    {
      label: "Last name",
      value: event.lastName || "--",
    },
    {
      label: "Phone number",
      value: event.phoneNumber || "--",
    },
    {
      label: "HRIS Status",
      value: (
        <Text
          variant="compact-description"
          className={event.employmentStatus === "ACTIVE" ? "" : "text-gray-7"}
        >
          {event.employmentStatus || "--"}
        </Text>
      ),
    } as InfoTableItem,
    {
      label: "HRIS start date",
      value: event.startDate
        ? format(parseISO(event.startDate), "M/d/yyyy h:mm a")
        : "--",
    } as InfoTableItem,
    {
      label: "HRIS termination date",
      value: event.terminationDate
        ? format(parseISO(event.terminationDate), "M/d/yyyy h:mm a")
        : "--",
    } as InfoTableItem,
  ];

  // Tabs for "Details", "Changes", and "Data"
  const tabKeys = ["details", "changes", "data"] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    changes: "Updated data",
    data: "All data",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={items} />,
    changes: () => (
      <div className="flex-1">
        {previousLoading ? (
          <div className="p-4 text-gray-7">Loading previous event...</div>
        ) : (
          <>
            <Text
              variant="compact-title"
              className="mb-2 text-gray-7"
              multiline
            >
              Diff of what changed in the external HRIS data between the
              currently viewed snapshot and the one before it.
            </Text>
            <div className="mt-4 " />
            <StyledJsonDiff jsonA={previousRawHrisData} jsonB={rawHrisData} />
          </>
        )}
      </div>
    ),
    data: () => (
      <div className="flex-1">
        <Text variant="compact-title" className="mb-2 text-gray-7" multiline>
          Raw data ingested from external HRIS system used in this snapshot
        </Text>
        <div className="mt-4 " />
        <FormattedJson data={rawHrisData} />
      </div>
    ),
  };

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={`Event ${event.pghId}: ${timestamp}`}
        sidebarType={DetailSidebarType.HrisEmployeeEvent}
      />
      <SidebarContent>
        <Text variant="compact-title" className="mb-2 text-gray-7" multiline>
          This is a historical snapshot of the HRIS Employee employee. A
          snapshot is created any time the employee's data is updated, which
          primarily only happens during a sync from the external HRIS system.
          All the below data is from the employee at the time of this snapshot.
        </Text>
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            defaultTab="details"
            queryParamKey="hris-employee-event-tab"
            contentContainerStyle={{ overflow: "hidden", paddingTop: 0 }}
          />
        </div>
      </SidebarContent>
    </div>
  );
};

// GraphQL query for the sidebar - fetches a single HRIS employee event by pghId
gql`
  query HrisEmployeeEventSidebar($pghId: Int!) {
    HrisEmployeeEvent(pghId: $pghId) {
      ...HrisEmployeeEventSidebar
    }
  }

  fragment HrisEmployeeEventSidebar on HrisEmployeeEvent {
    pghId
    pghCreatedAt
    remoteHrisId
    rawHrisData
    id
    firstName
    lastName
    phoneNumber
    employmentStatus
    startDate
    terminationDate
  }

  query HrisEmployeeEventPrevious(
    $input: HrisEmployeeEventsInput!
    $pagination: PaginationInput
  ) {
    HrisEmployeeEvents(input: $input, pagination: $pagination) {
      objects {
        ...HrisEmployeeEventSidebar
      }
    }
  }
`;

export default HrisEmployeeEventDetailSidebar;
